import type { ContactDetailResponseDto } from "@shared/generated/crm/models/ContactDetailResponseDto";
import type { ContactFieldSignalResponseDto } from "@shared/generated/crm/models/ContactFieldSignalResponseDto";
import type { LeadResponseDto } from "@shared/generated/crm/models/LeadResponseDto";
import type {
  LeadSignalResponseDto,
  LeadSignalResponseDtoTypeEnumKey,
} from "@shared/generated/crm/models/LeadSignalResponseDto";
import type {
  LeadGroup,
  NextStepItem,
  SidekickUpdateCard,
  SidekickUpdateItem,
  SignalItem,
} from "./contacts.types";
import { INTENT_LABELS } from "./lead-formatters";

// ---------------------------------------------------------------------------
// Source formatting
// ---------------------------------------------------------------------------

const SIGNAL_SOURCE_LABELS = {
  "email:signature": "from email signature",
  "email:body": "from email body",
  "email:headers": "from email headers",
  "email:subject": "from email subject",
  "email:quoted_text": "from quoted email text",
  "email:forward-header": "forwarded email",
  "user:input": "entered by you",
  "user:reviewed": "confirmed by you",
} as const satisfies Record<string, string>;

const BRIEFING_SOURCE_LABELS = {
  signals_bound: "Gmail",
} as const satisfies Record<string, string>;

// "user_provided" means at least one field has a user-entered value — it can mean
// the contact was added manually, or that a Gmail-sourced contact was later edited.
export function formatBriefingSource({
  source,
  sources,
}: {
  source: string;
  sources: string[];
}): string {
  if (source === "user_provided") {
    return sources.includes("signals_bound")
      ? "Edited manually"
      : "Added manually";
  }
  if (source in BRIEFING_SOURCE_LABELS)
    return BRIEFING_SOURCE_LABELS[
      source as keyof typeof BRIEFING_SOURCE_LABELS
    ];
  return source;
}

function formatSignalSource(source: string): string {
  if (source in SIGNAL_SOURCE_LABELS)
    return SIGNAL_SOURCE_LABELS[source as keyof typeof SIGNAL_SOURCE_LABELS];
  const colonIndex = source.indexOf(":");
  if (colonIndex !== -1) {
    const channel = source.slice(0, colonIndex);
    const detail = source
      .slice(colonIndex + 1)
      .replace(/_/g, " ")
      .replace(/-/g, " ");
    return `${channel} (${detail})`;
  }
  return source;
}

// ---------------------------------------------------------------------------
// Contact field signal mappers
// ---------------------------------------------------------------------------

const UPDATE_SIGNAL_TYPES = new Set([
  // Identity
  "full_name",
  "first_name",
  "last_name",
  "preferred_name",
  // Contact
  "email",
  "phone",
  "links",
  "street_address",
  "city",
  "state",
  "postal_code",
  // Professional
  "job_title",
  "seniority",
  "department",
  "role_type",
  // Company
  "company_name",
  "company_domain",
  "company_size",
  "company_industry",
  // Location / Context
  "timezone",
  "country",
]);

const CONTACT_FIELD_MAP: Partial<
  Record<string, (c: ContactDetailResponseDto) => string | null | undefined>
> = {
  job_title: (c) => c.title,
  company_name: (c) => c.company,
  full_name: (c) => c.fullName,
  first_name: (c) => c.firstName,
  last_name: (c) => c.lastName,
  preferred_name: (c) => c.preferredName,
  city: (c) => c.city,
  state: (c) => c.state,
  postal_code: (c) => c.postalCode,
  street_address: (c) => c.address,
  timezone: (c) => c.timezone,
};

type ContactFieldSignalMapper = (
  signal: ContactFieldSignalResponseDto,
  before: string | null,
) => SignalItem;

function makeContactFieldMapper(label: string): ContactFieldSignalMapper {
  return (signal, before) => {
    const text = before
      ? `${before} → ${signal.value}`
      : `Updated to ${signal.value}`;
    return {
      kind: "signal",
      label,
      text,
      occurrenceDate: signal.occurredAt,
      sourceLabel: formatSignalSource(signal.source),
      interactionId: signal.interactionId,
      source: signal.source,
    };
  };
}

const CONTACT_FIELD_SIGNAL_MAPPERS = {
  full_name: makeContactFieldMapper("Full Name"),
  first_name: makeContactFieldMapper("First Name"),
  last_name: makeContactFieldMapper("Last Name"),
  preferred_name: makeContactFieldMapper("Preferred Name"),
  email: makeContactFieldMapper("Email"),
  phone: makeContactFieldMapper("Phone"),
  links: makeContactFieldMapper("Links"),
  street_address: makeContactFieldMapper("Street Address"),
  city: makeContactFieldMapper("City"),
  state: makeContactFieldMapper("State"),
  postal_code: makeContactFieldMapper("Postal Code"),
  job_title: makeContactFieldMapper("Job Title"),
  seniority: makeContactFieldMapper("Seniority"),
  department: makeContactFieldMapper("Department"),
  role_type: makeContactFieldMapper("Role Type"),
  company_name: makeContactFieldMapper("Company"),
  company_domain: makeContactFieldMapper("Company Domain"),
  company_size: makeContactFieldMapper("Company Size"),
  company_industry: makeContactFieldMapper("Industry"),
  timezone: makeContactFieldMapper("Timezone"),
  country: makeContactFieldMapper("Country"),
} as const satisfies Record<string, ContactFieldSignalMapper>;

const isValidContactFieldSignalKey = (
  key: string,
): key is keyof typeof CONTACT_FIELD_SIGNAL_MAPPERS => {
  return key in CONTACT_FIELD_SIGNAL_MAPPERS;
};

export function buildNotableUpdatesCard(
  signals: NonNullable<ContactDetailResponseDto["contactFieldSignals"]>,
  contact: ContactDetailResponseDto,
): SidekickUpdateCard | null {
  const byType = new Map<
    string,
    NonNullable<ContactDetailResponseDto["contactFieldSignals"]>
  >();
  for (const s of signals) {
    if (!UPDATE_SIGNAL_TYPES.has(s.type)) continue;
    const bucket = byType.get(s.type) ?? [];
    bucket.push(s);
    byType.set(s.type, bucket);
  }

  if (byType.size === 0) {
    const fallback: SignalItem[] = [
      ...(contact.email ?? []).map((e) => ({
        kind: "signal" as const,
        label: "Email",
        text: `${e.label}: ${e.value}`,
      })),
      ...(contact.phone ?? []).map((p) => ({
        kind: "signal" as const,
        label: "Phone",
        text: `${p.label}: ${p.value}`,
      })),
      ...(contact.links ?? []).map((l) => ({
        kind: "signal" as const,
        label: "Link",
        text: `${l.label}: ${l.value}`,
      })),
    ].slice(0, 3);
    if (fallback.length === 0) return null;
    return {
      id: "notable-updates",
      title: "Notable Updates Detected for CRM",
      content: fallback,
    };
  }

  const signalsWithBeforeValue = [...byType.entries()].map(
    ([type, typeSigs]) => {
      const sorted = [...typeSigs].sort((a, b) =>
        b.occurredAt < a.occurredAt ? -1 : 1,
      );
      const newest = sorted[0];
      const olderSignalValue = sorted[1]?.value ?? null;
      const currentCrmValue = CONTACT_FIELD_MAP[type]?.(contact) ?? null;
      const before =
        olderSignalValue ??
        (currentCrmValue && currentCrmValue !== newest.value
          ? currentCrmValue
          : null);
      return { type, newest, before };
    },
  );

  const top3 = signalsWithBeforeValue
    .sort((a, b) => (b.newest.occurredAt < a.newest.occurredAt ? -1 : 1))
    .slice(0, 3);

  const content = top3.flatMap(({ type, newest, before }) => {
    if (!isValidContactFieldSignalKey(type)) return [];
    const mapper =
      CONTACT_FIELD_SIGNAL_MAPPERS[type] ?? makeContactFieldMapper(type);
    return [mapper(newest, before)];
  });

  return {
    id: "notable-updates",
    title: "Notable Updates Detected for CRM",
    content,
  };
}

// ---------------------------------------------------------------------------
// Lead signal mappers
// ---------------------------------------------------------------------------

type LeadSignalMapper = (signal: LeadSignalResponseDto) => SignalItem;

function makeLeadSignalMapper(label: string): LeadSignalMapper {
  return (signal) => {
    return {
      kind: "signal",
      label,
      text: signal.quotedText ? `"${signal.quotedText}"` : "",
      occurrenceDate: signal.occurredAt,
      sourceLabel: formatSignalSource(signal.source),
      interactionId: signal.interactionId,
      source: signal.source,
    };
  };
}

export const LEAD_SIGNAL_MAPPERS = {
  INTEREST_EXPRESSED: makeLeadSignalMapper("Showed interest"),
  PROPERTY_NEEDS_DESCRIBED: makeLeadSignalMapper("Shared property needs"),
  TIMELINE_MENTIONED: makeLeadSignalMapper("Timeline shared"),
  MARKET_ANALYSIS_INQUIRED: makeLeadSignalMapper("Market inquiry"),
  PROPERTY_VALUATION_REQUESTED: makeLeadSignalMapper("Wants valuation"),
  AGENT_FEES_INQUIRED: makeLeadSignalMapper("Asked about fees"),
  CLOSING_COSTS_INQUIRED: makeLeadSignalMapper("Asked about costs"),
  FINANCING_DISCUSSED: makeLeadSignalMapper("Discussed financing"),
  APPOINTMENT_INTENT_DETECTED: makeLeadSignalMapper("Wants to meet"),
  PROPERTY_REFERENCED: makeLeadSignalMapper("Mentioned property"),
  OFFER_DISCUSSED: makeLeadSignalMapper("Offer mentioned"),
  NEGOTIATION_STARTED: makeLeadSignalMapper("Negotiating"),
  TRANSACTION_STEP_OBSERVED: makeLeadSignalMapper("Transaction progressing"),
  LEASE_TERMS_DISCUSSED: makeLeadSignalMapper("Discussing lease terms"),
  DELAY_INDICATED: makeLeadSignalMapper("Delay mentioned"),
  NO_INTEREST_EXPRESSED: makeLeadSignalMapper("Not interested"),
} as const satisfies Record<LeadSignalResponseDtoTypeEnumKey, LeadSignalMapper>;

function formatLeadIntent(intent: string): string {
  return INTENT_LABELS[intent.toUpperCase()] ?? intent;
}

export function buildOpportunitiesCard(
  leads: LeadResponseDto[],
): SidekickUpdateCard | null {
  const openLeads = leads
    .filter((l) => !l.closedAt)
    .sort((a, b) => {
      const aTime = a.lastSignalAt ? new Date(a.lastSignalAt).getTime() : 0;
      const bTime = b.lastSignalAt ? new Date(b.lastSignalAt).getTime() : 0;
      return bTime - aTime;
    });

  if (openLeads.length === 0) return null;

  const leadGroups: LeadGroup[] = openLeads.flatMap((lead) => {
    const content: SidekickUpdateItem[] = [];

    const recentSignals = (lead.leadSignals ?? [])
      .filter(
        (s) => s.status !== "superseded" && s.type !== "NO_INTEREST_EXPRESSED",
      )
      .sort(
        (a, b) =>
          new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      )
      .slice(0, 3);

    for (const signal of recentSignals) {
      const mapper = LEAD_SIGNAL_MAPPERS[signal.type];
      content.push(mapper(signal));
    }

    if (lead.nextStep) {
      const item: NextStepItem = {
        kind: "next-step",
        description: lead.nextStep.description,
        dueAt: lead.nextStep.dueAt ?? undefined,
      };
      content.push(item);
    }

    if (content.length === 0) return [];

    return [
      {
        id: lead.id,
        label: formatLeadIntent(lead.intent),
        lastSignalAt: lead.lastSignalAt,
        content,
      },
    ];
  });

  if (leadGroups.length === 0) return null;

  return {
    id: "opportunities",
    title: "Opportunities & Signals",
    content: [],
    leadGroups,
  };
}
